import dayjs from 'dayjs';
import { CalendarDimensions } from '@enums';
import { CalendarSettingsType } from '../calendar-types';
import { getSecondsInPixel } from './get-seconds-in-pixel';

export function getInitialScrollOffset(
  currentDateUnix: number,
  settings: Partial<CalendarSettingsType>,
  targetDateUnix?: number
) {
  const {
    dimension = CalendarDimensions.WEEK,
    startingHour = 0,
    endingHour = 24,
  } = settings;

  const secondsInPixel = getSecondsInPixel(settings);

  const currentDate = dayjs.unix(currentDateUnix).startOf('day');
  const now = targetDateUnix ? dayjs.unix(targetDateUnix) : dayjs();

  const hoursOffset = 0; // we want to scroll to the beginning of the day

  switch (dimension) {
    case CalendarDimensions.DAY: {
      if (
        !currentDate.isSame(now, 'day') ||
        now.hour() < startingHour ||
        now.hour() > endingHour
      ) {
        return 0; // No offset if not the same day or outside working hours
      }

      const dayStart = currentDate.add(startingHour, 'hour');
      const secondsFromDayStart = now.diff(dayStart, 'second');

      return secondsFromDayStart / secondsInPixel;
    }
    case CalendarDimensions.WEEK: {
      const dayDiff = now.startOf('day').diff(currentDate, 'day');
      if (dayDiff < 0 || dayDiff > 6) {
        return 0;
      }

      const visibleHoursPerDay = (endingHour ?? 24) - (startingHour ?? 0);
      const dayWidthInPixels = visibleHoursPerDay * (settings.stepWidth ?? 100);

      const offsetForDays = dayDiff * dayWidthInPixels;

      // If targetDateUnix is not provided, scroll to the start of the day. Otherwise, scroll to the specified time.
      const targetTime = targetDateUnix
        ? now
        : now.startOf('day').hour(startingHour);

      // Calculate seconds from the start of the visible part of the day
      const secondsIntoDay = targetTime.diff(
        targetTime.startOf('day').add(startingHour, 'hour'),
        'second'
      );
      const offsetForTime = secondsIntoDay / secondsInPixel;

      return offsetForDays + offsetForTime;
    }

    case CalendarDimensions.MONTH: {
      if (!currentDate.isSame(now, 'month')) {
        return 0;
      }

      const start = dayjs(new Date());
      const end = dayjs(new Date()).startOf('month');

      const diff = dayjs.duration(start.diff(end)).asHours();

      return (60 * 60 * (diff - hoursOffset)) / secondsInPixel;
    }

    default:
      return 0;
  }
}
